import { Action } from 'redux'
import { ThunkAction, ThunkDispatch } from 'redux-thunk'
import { generateApiActionTypes } from 'utils/actions/generateActionTypes'
import { decamelizeKeysWithNumbers } from 'utils/api/decamelizeKeysWithNumbers'
import { FP_ENDPOINTS } from 'apps/fleet-planner/config'
import * as FacilitiesApi from 'apps/fleet-planner/utils/api/facilities'
import {
  IGetRecentFacilitiesSuccessAction,
  IUpdateFacilitySuccessAction,
  IUpdateFacilityAddressSuccessAction,
  IUpdateFacilityContactSuccessAction,
  IUpdateFacilityAddressFailureAction,
  IUpdateFacilityFailureAction,
  IUpdateFacilityContactFailureAction,
  ICreateFacilityContactSuccessAction,
  ICreateFacilityContactFailureAction,
  ICreateNewFacilitySuccessAction,
  IFetchFacilitySuccessAction,
} from 'apps/fleet-planner/utils/types/facilities'
import { IStore } from 'apps/fleet-planner/utils/types/store'

export const GET_RECENT_FACILITIES_ACTION_TYPES = generateApiActionTypes('GET_RECENT_FACILITIES')
export const CREATE_NEW_FACILITY_ACTION_TYPES = generateApiActionTypes(
  'CREATE_NEW_FACILITY_ACTION_TYPES'
)
export const UPDATE_FACILITY_ACTION_TYPES = generateApiActionTypes('UPDATE_FACILITY')
export const UPDATE_FACILITY_ADDRESS_ACTION_TYPES = generateApiActionTypes(
  'UPDATE_FACILITY_ADDRESS'
)
export const CREATE_FACILITY_CONTACT_ACTION_TYPES = generateApiActionTypes(
  'CREATE_FACILITY_CONTACT'
)
export const UPDATE_FACILITY_CONTACT_ACTION_TYPES = generateApiActionTypes(
  'UPDATE_FACILITY_CONTACT'
)
export const COMPLEX_UPDATE_FACILITY_ACTION_TYPES = generateApiActionTypes(
  'COMPLEX_UPDATE_FACILITY'
)

export const getFacilitiesSuccess = (
  facilities: FacilitiesApi.IFacility[]
): IFetchFacilitySuccessAction => {
  const { GET_RECENT_FACILITIES_SUCCESS } = GET_RECENT_FACILITIES_ACTION_TYPES
  return {
    type: GET_RECENT_FACILITIES_SUCCESS,
    payload: {
      facilities,
    },
  }
}

export const getRecentFacilities = (params: FacilitiesApi.IGetFacilitiesParams = {}) => ({
  payload: {
    callApi: true,
    requestData: {
      endpoint: FP_ENDPOINTS.facilities,
      method: 'GET',
      params,
    },
    actionTypes: Object.keys(GET_RECENT_FACILITIES_ACTION_TYPES),
  },
  type: '',
})

export const createNewFacility = (body: FacilitiesApi.ICreateNewFacilityRequest) => ({
  payload: {
    callApi: true,
    requestData: {
      endpoint: FP_ENDPOINTS.facilities,
      method: 'POST',
      body: decamelizeKeysWithNumbers(body),
    },
    actionTypes: Object.keys(CREATE_NEW_FACILITY_ACTION_TYPES),
  },
  type: '',
})

export const createNewFacilitySuccess = (
  facility: FacilitiesApi.IFacility
): ICreateNewFacilitySuccessAction => {
  const { CREATE_NEW_FACILITY_SUCCESS } = CREATE_NEW_FACILITY_ACTION_TYPES
  return {
    type: CREATE_NEW_FACILITY_SUCCESS,
    payload: {
      facility,
    },
  }
}

export const updateFacilitySuccess = (
  facility: FacilitiesApi.IFacility
): IUpdateFacilitySuccessAction => {
  const { UPDATE_FACILITY_SUCCESS } = UPDATE_FACILITY_ACTION_TYPES
  return {
    type: UPDATE_FACILITY_SUCCESS,
    payload: {
      facility,
    },
  }
}

export const updateFacility = (facilityId: string, body: FacilitiesApi.IUpdateFacilityRequest) => ({
  payload: {
    callApi: true,
    requestData: {
      endpoint: `${FP_ENDPOINTS.facilities}/${facilityId}`,
      method: 'PUT',
      body: decamelizeKeysWithNumbers(body),
    },
    actionTypes: Object.keys(UPDATE_FACILITY_ACTION_TYPES),
  },
  type: '',
})

export const updateFacilityAddress = (
  facilityId: string,
  addressId: string,
  body: FacilitiesApi.IUpdateFacilityAddressRequest
) => ({
  payload: {
    callApi: true,
    requestData: {
      endpoint: `${FP_ENDPOINTS.facilities}/${facilityId}/addresses/${addressId}`,
      method: 'PUT',
      body: decamelizeKeysWithNumbers(body),
    },
    actionTypes: Object.keys(UPDATE_FACILITY_ADDRESS_ACTION_TYPES),
  },
  type: '',
})

export const createFacilityContact = (
  facilityId: string,
  body: FacilitiesApi.ICreateFacilityContactRequest
) => ({
  payload: {
    callApi: true,
    requestData: {
      endpoint: `${FP_ENDPOINTS.facilities}/${facilityId}/contacts`,
      method: 'POST',
      body: decamelizeKeysWithNumbers(body),
    },
    actionTypes: Object.keys(CREATE_FACILITY_CONTACT_ACTION_TYPES),
  },
  type: '',
})

export const updateFacilityContact = (
  facilityId: string,
  contactId: string,
  body: FacilitiesApi.IUpdateFacilityContactRequest
) => ({
  payload: {
    callApi: true,
    requestData: {
      endpoint: `${FP_ENDPOINTS.facilities}/${facilityId}/contacts/${contactId}`,
      method: 'PUT',
      body: decamelizeKeysWithNumbers(body),
    },
    actionTypes: Object.keys(UPDATE_FACILITY_CONTACT_ACTION_TYPES),
  },
  type: '',
})

export const updateFacilityAddressThunk = (
  facilityId: string,
  addressId: string,
  body: FacilitiesApi.IUpdateFacilityAddressRequest
) => (dispatch: ThunkDispatch<IStore, any, any>) => {
  return dispatch(updateFacilityAddress(facilityId, addressId, body))
}

export const updateFacilityThunk = (
  facilityId: string,
  addressId: string | undefined, // not optional, but can be undefined
  contactId: string | undefined, // not optional, but can be undefined
  update: {
    facility?: FacilitiesApi.IUpdateFacilityRequest
    address?: FacilitiesApi.IUpdateFacilityAddressRequest
    contact?: FacilitiesApi.IUpdateFacilityContactRequest
  }
): ThunkAction<void, IStore, unknown, Action<string>> => (
  dispatch: ThunkDispatch<IStore, any, any>
) => {
  if (!facilityId) return
  const { address, contact, facility } = update || {}
  const requests: any = []
  const {
    COMPLEX_UPDATE_FACILITY_FAILURE,
    COMPLEX_UPDATE_FACILITY_REQUEST,
    COMPLEX_UPDATE_FACILITY_SUCCESS,
  } = COMPLEX_UPDATE_FACILITY_ACTION_TYPES

  dispatch({ type: COMPLEX_UPDATE_FACILITY_REQUEST })

  // Update address
  if (address && addressId && !!Object.keys(address).length) {
    const {
      UPDATE_FACILITY_ADDRESS_FAILURE,
      UPDATE_FACILITY_ADDRESS_SUCCESS,
    } = UPDATE_FACILITY_ADDRESS_ACTION_TYPES
    requests.push(
      FacilitiesApi.updateFacilityAddress(facilityId, addressId, address)
        .then(({ address: updatedAddress }) =>
          dispatch<IUpdateFacilityAddressSuccessAction>({
            type: UPDATE_FACILITY_ADDRESS_SUCCESS,
            payload: {
              address: updatedAddress,
            },
          })
        )
        .catch(error =>
          dispatch<IUpdateFacilityAddressFailureAction>({
            type: UPDATE_FACILITY_ADDRESS_FAILURE,
            error,
          })
        )
    )
  }

  const isExistingContact: boolean = !!contactId && contactId !== 'undefined'
  const isValidContact: boolean =
    !!contact && !!Object.keys(contact).length && !!(contact.name || contact.phone || contact.email)

  // Update contact
  if (isValidContact && isExistingContact && !!contactId && !!contact) {
    const {
      UPDATE_FACILITY_CONTACT_FAILURE,
      UPDATE_FACILITY_CONTACT_SUCCESS,
    } = UPDATE_FACILITY_CONTACT_ACTION_TYPES
    requests.push(
      FacilitiesApi.updateFacilityContact(facilityId, contactId, contact)
        .then(({ contact: updatedContact }) =>
          dispatch<IUpdateFacilityContactSuccessAction>({
            type: UPDATE_FACILITY_CONTACT_SUCCESS,
            payload: { contact: updatedContact },
          })
        )
        .catch(error =>
          dispatch<IUpdateFacilityContactFailureAction>({
            type: UPDATE_FACILITY_CONTACT_FAILURE,
            error,
          })
        )
    )
  } else if (isValidContact && !!contact) {
    // If ID is undefined, create a new contact
    const {
      CREATE_FACILITY_CONTACT_FAILURE,
      CREATE_FACILITY_CONTACT_SUCCESS,
    } = CREATE_FACILITY_CONTACT_ACTION_TYPES
    requests.push(
      FacilitiesApi.createFacilityContact(facilityId, contact)
        .then(({ contact: updatedContact }) =>
          dispatch<ICreateFacilityContactSuccessAction>({
            type: CREATE_FACILITY_CONTACT_SUCCESS,
            payload: { contact: updatedContact },
          })
        )
        .catch(error =>
          dispatch<ICreateFacilityContactFailureAction>({
            type: CREATE_FACILITY_CONTACT_FAILURE,
            error,
          })
        )
    )
  }

  // Update facility
  if (facility && !!Object.keys(facility)) {
    const { UPDATE_FACILITY_FAILURE, UPDATE_FACILITY_SUCCESS } = UPDATE_FACILITY_ACTION_TYPES
    requests.push(
      FacilitiesApi.updateFacility(facilityId, facility)
        .then(({ facility: updatedFacility }) => {
          dispatch<IUpdateFacilitySuccessAction>({
            type: UPDATE_FACILITY_SUCCESS,
            payload: { facility: updatedFacility },
          })
        })
        .catch(error =>
          dispatch<IUpdateFacilityFailureAction>({ type: UPDATE_FACILITY_FAILURE, error })
        )
    )
  }

  const { GET_RECENT_FACILITIES_SUCCESS } = GET_RECENT_FACILITIES_ACTION_TYPES

  Promise.all(requests)
    .then(() =>
      FacilitiesApi.getRecentFacilities({ pageSize: 1000 })
        .then(({ facilities }) => {
          dispatch<IGetRecentFacilitiesSuccessAction>({
            type: GET_RECENT_FACILITIES_SUCCESS,
            payload: { facilities },
          })
        })
        .then(() => dispatch({ type: COMPLEX_UPDATE_FACILITY_SUCCESS }))
    )
    .catch(error => dispatch({ type: COMPLEX_UPDATE_FACILITY_FAILURE, error }))
}
